import { ChangeEvent, FormEvent, useState } from "react";
import { useSelector } from "react-redux";
import {
  GenerateReportRequestDto as GenerateReport,
  ReportType,
  useGenerateReportMutation,
  useGetReportTypesQuery,
} from "../../actions/report.action";
import Alert from "../../components/alert";
import Button from "../../components/button";
import {
  GroupInputField,
  InputField,
  Select,
} from "../../components/input-functions";
import { ReduxState } from "../../reducers";
import { getTodayDateForInputField } from "../../utils";
import FormField from "./formField";
import getStartDateForLender, { formatString } from "./helper";

type GenerateReportFormProps = {
  isPreProd?: boolean;
};

export default function GenerateReportForm({
  isPreProd = false,
}: GenerateReportFormProps) {
  const [triggerGenerateReportMutation, { isLoading: isGeneratingReport }] =
    useGenerateReportMutation();
  const {
    data: reportTypes,
    isLoading: isLoadingReportTypes,
    error: reportTypesError,
  } = useGetReportTypesQuery();

  const lenderId: string = useSelector(
    (state: ReduxState) => state.admin.data?.lenderId,
  );

  const [formData, setFormData] = useState<GenerateReport>({
    reportType: ReportType.BUREAU_REPORT,
    fromDate: "",
    toDate: "",
  });
  const [isSinceInception, setIsSinceInception] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  function handleChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const target = e.target;
    if (target instanceof HTMLInputElement && target.type === "checkbox") {
      const checked = target.checked;
      setIsSinceInception(checked);
      if (checked) {
        setFormData((prev) => ({
          ...prev,
          fromDate: getStartDateForLender(lenderId),
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          fromDate: "",
        }));
      }
      return;
    }
    setFormData((prevState) => ({ ...prevState, [target.name]: target.value }));
  }
  async function handleFormSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    try {
      const { reportType, fromDate, toDate } = formData;
      // NOTE: Dates created in local time (no 'Z') to align with backend timezone conversion.
      // When converted to ISO, local midnight becomes UTC timestamp that backend interprets correctly.
      const fromDateLocal = new Date(fromDate + "T00:00:00.000");
      const toDateLocal = new Date(toDate + "T23:59:59.999");
      const payload: GenerateReport = {
        reportType,
        fromDate: fromDateLocal.toISOString(),
        toDate: toDateLocal.toISOString(),
        ...(isPreProd && { isPreProd }),
      };

      await triggerGenerateReportMutation(payload).unwrap();
      setSuccessMessage("Report generated successfully");
    } catch (error) {
      setErrorMessage(
        error?.data?.message ||
          error?.error ||
          "Something went wrong, Try again later",
      );
    }
  }
  return (
    <form
      style={{ display: "flex", flexDirection: "column", gap: "20px" }}
      onSubmit={(e) => handleFormSubmit(e)}
      className="dashboard-container"
    >
      <Alert
        type="danger"
        canDismiss
        message={errorMessage}
        onDismissClick={() => setErrorMessage("")}
      />
      <Alert
        type="success"
        canDismiss
        message={successMessage}
        onDismissClick={() => setSuccessMessage("")}
      />
      <FormField label="Report Type">
        <Select
          name="reportType"
          style={{ width: "40%" }}
          onChange={handleChange}
          value={formData.reportType}
          placeholder="Select Report Type"
          options={reportTypes?.map((reportType) => ({
            value: reportType,
            label: formatString({
              inputString: reportType,
              customStringValue:
                reportType === "REVENUE_REPORT_PI_COLLECTION"
                  ? "Revenue Report Principal/ Interest Collected"
                  : "",
            }),
          }))}
        />
      </FormField>
      <div className="d-flex align-items-center justify-content-between">
        <FormField label="Date Range">
          <GroupInputField
            onChange={handleChange}
            fields={[
              {
                element: "input",
                type: "date",
                placeholder: "Select from date",
                name: "fromDate",
                value: formData.fromDate,
                required: true,
                disabled: isSinceInception,
                max: getTodayDateForInputField(),
              },
              {
                element: "input",
                type: "date",
                placeholder: "Select to date",
                name: "toDate",
                value: formData.toDate,
                required: true,
                min: formData.fromDate,
                max: getTodayDateForInputField(),
              },
            ]}
          />
        </FormField>
        <div className="d-flex flex-column">
          <div className="d-flex align-items-center">
            <InputField
              name="checkbox"
              type="checkbox"
              checked={isSinceInception}
              onChange={handleChange}
            />
            <p className="ml-2 mt-3">
              Set start date as{" "}
              <i>
                <b>since inception</b>
              </i>
            </p>
          </div>
          <Button variant="primary" loading={isGeneratingReport}>
            Generate
          </Button>
        </div>
      </div>
    </form>
  );
}
